import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, MapPin, Pencil, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { formatDateTime } from "../lib/format";

export default function AdminLakeReviewsPage() {
  const [dirQ, setDirQ] = useState("");
  const [dirSearched, setDirSearched] = useState("");

  const { data: dirResults = [], isFetching: dirFetching } = useQuery({
    queryKey: ["admin_canonical_lake_search", dirSearched],
    queryFn: () =>
      dirSearched.trim().length < 2
        ? Promise.resolve([])
        : fetch(
            `/app-api/admin/canonical-lakes/search?q=${encodeURIComponent(dirSearched.trim())}`,
          ).then((r) => (r.ok ? r.json() : [])),
    enabled: dirSearched.trim().length >= 2,
    staleTime: 30_000,
  });

  const {
    data: reviews = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin_lake_reviews"],
    queryFn: () =>
      fetch("/app-api/admin/lake-reviews").then((r) => {
        if (!r.ok) throw new Error("Forbidden or unavailable");
        return r.json();
      }),
  });

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/admin"
          className="text-sm font-medium text-accent hover:underline"
        >
          ← Admin
        </Link>
        <div className="mt-2 flex min-w-0 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-secondary">
            <ClipboardList />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-primary">
              Community Lake Reviews
            </h1>
            <p className="mt-1 text-sm text-secondary">
              Review, approve, link or reject public lake submissions.
            </p>
          </div>
        </div>
      </header>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-raised p-6 text-center text-sm text-secondary">
          Loading submissions…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-error bg-error-weak p-4 text-sm text-error">
          Could not load submissions.{" "}
          <Button variant="tertiary" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <ClipboardList className="mx-auto text-secondary" />
          <p className="mt-3 text-sm text-secondary">
            No pending lake submissions.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-secondary">
            {reviews.length} pending{" "}
            {reviews.length === 1 ? "submission" : "submissions"}
          </p>
          <div className="divide-y divide-border rounded-lg border border-border bg-raised">
            {reviews.map((review: any) => (
              <Link
                key={review.id}
                to={`/admin/lake-reviews/${encodeURIComponent(review.id)}`}
                className="block px-4 py-4 transition-colors hover:bg-inset"
              >
                <div className="flex min-w-0 items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-primary">
                      {review.canonical_name}
                    </p>
                    {review.venue_name ? (
                      <p className="mt-0.5 truncate text-sm text-secondary">
                        {review.venue_name}
                      </p>
                    ) : null}
                    <p className="mt-1 flex min-w-0 items-center gap-1 truncate text-sm text-secondary">
                      <MapPin className="size-3.5 shrink-0" />
                      {[review.postcode, review.county_region, review.country]
                        .filter(Boolean)
                        .join(", ") || "Location not recorded"}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="inline-flex items-center rounded-full bg-warning-weak px-2 py-0.5 text-xs font-medium text-warning">
                      Pending
                    </span>
                    <p className="mt-1 text-xs text-secondary">
                      {formatDateTime(review.created_at)}
                    </p>
                  </div>
                </div>
                {review.submitted_by_name ? (
                  <p className="mt-2 text-xs text-secondary">
                    Submitted by {review.submitted_by_name}
                  </p>
                ) : null}
              </Link>
            ))}
          </div>
        </div>
      )}

      <section className="space-y-3 border-t border-border pt-6">
        <h2 className="text-base font-semibold text-primary">
          Edit a directory lake
        </h2>
        <p className="text-sm text-secondary">
          Search approved lakes to correct name, venue, or location details.
        </p>
        <div className="flex gap-2">
          <Input
            value={dirQ}
            onChange={(e) => setDirQ(e.target.value)}
            placeholder="Lake or venue name"
            onKeyDown={(e) => {
              if (e.key === "Enter") setDirSearched(dirQ);
            }}
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={() => setDirSearched(dirQ)}
            isLoading={dirFetching}
          >
            <Search className="size-4" />
            Search
          </Button>
        </div>
        {dirSearched.trim().length >= 2 &&
          !dirFetching &&
          dirResults.length === 0 && (
            <p className="text-sm text-secondary">No approved lakes matched.</p>
          )}
        {dirResults.length > 0 && (
          <div className="divide-y divide-border rounded-lg border border-border bg-raised">
            {dirResults.map((lake: any) => (
              <div
                key={lake.id}
                className="flex min-w-0 items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-primary">
                    {lake.canonical_name}
                  </p>
                  {lake.venue_name ? (
                    <p className="truncate text-xs text-secondary">
                      {lake.venue_name}
                    </p>
                  ) : null}
                  <p className="flex min-w-0 items-center gap-1 truncate text-xs text-secondary">
                    <MapPin className="size-3 shrink-0" />
                    {[lake.postcode, lake.county_region]
                      .filter(Boolean)
                      .join(", ") || "Location not recorded"}
                  </p>
                </div>
                <Button variant="secondary" size="sm" asChild>
                  <Link
                    to={`/admin/canonical-lakes/${encodeURIComponent(lake.id)}/edit`}
                  >
                    <Pencil className="size-3.5" />
                    Edit
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
